import React from 'react';
import { motion } from 'framer-motion';
import { Footer } from './LandingPage';
import { Shield, Lock, DatabaseZap, Server, ShieldCheck, KeyRound } from 'lucide-react';

const PageHeader = ({ title, subtitle }) => (
  <div className="relative bg-green-900/20 py-20 sm:py-32">
    <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
    <div className="container mx-auto px-4 text-center relative z-10">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="text-4xl md:text-6xl font-bold tracking-tight text-white"
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-green-100/80"
      >
        {subtitle}
      </motion.p>
    </div>
  </div>
);

const SecurityDetailsSection = () => {
  const securityFeatures = [
    {
      icon: <Lock className="w-8 h-8 text-amber-400" />,
      title: "Criptografia de Ponta a Ponta",
      description: "Todos os seus dados, tanto em trânsito quanto em repouso, são protegidos com criptografia AES-256, o mesmo padrão utilizado por bancos e governos."
    },
    {
      icon: <Server className="w-8 h-8 text-amber-400" />,
      title: "Infraestrutura Robusta e Segura",
      description: "Nossos servidores estão hospedados em data centers de classe mundial (AWS) que contam com segurança física e digital 24/7, garantindo máxima disponibilidade e proteção."
    },
    {
      icon: <KeyRound className="w-8 h-8 text-amber-400" />,
      title: "Controle de Acesso Granular",
      description: "Você decide quem vê o quê. Crie perfis de usuário personalizados (gerente, operador, etc.) e defina permissões específicas para cada módulo e funcionalidade."
    },
    {
      icon: <DatabaseZap className="w-8 h-8 text-amber-400" />,
      title: "Backups e Recuperação de Desastres",
      description: "Realizamos backups automáticos e redundantes dos seus dados diariamente. Em caso de qualquer eventualidade, seus dados podem ser restaurados de forma rápida e segura."
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-amber-400" />,
      title: "Conformidade com a LGPD",
      description: "Levamos a Lei Geral de Proteção de Dados a sério. Nossa plataforma e políticas são desenhadas para garantir a privacidade e o controle total sobre suas informações pessoais e de negócio."
    },
    {
      icon: <Shield className="w-8 h-8 text-amber-400" />,
      title: "Monitoramento e Auditoria Constante",
      description: "Nossa equipe de segurança monitora a plataforma continuamente contra ameaças e vulnerabilidades. Todas as ações importantes geram logs para auditoria e rastreabilidade."
    }
  ];

  return (
    <div className="py-20 sm:py-24 bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Nossos Compromissos com a sua Segurança</h2>
            <p className="mt-4 text-lg text-green-200/70">
                A confiança é a base do nosso negócio. Veja as camadas de proteção que garantem a integridade e a confidencialidade dos seus dados.
            </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
              className="bg-green-900/20 border border-green-800/30 rounded-lg p-8 h-full"
            >
              <div className="mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-green-100/80">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Seguranca() {
  return (
    <div className="bg-gray-950">
      <PageHeader 
        title="Sua Fazenda, Seus Dados, Nossa Proteção"
        subtitle="No AgroGestão, a segurança dos seus dados não é um recurso, é o nosso alicerce."
      />
      <SecurityDetailsSection />
      <Footer />
    </div>
  );
}
